import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { questionnaireService, adminService, authService } from '../services/api';
import { toast } from 'sonner';
import { Plus, FileText, Filter, X, Pencil, Trash2 } from 'lucide-react';
import { AutocompleteInput } from '../components/AutocompleteInput';
import { Pagination } from '../components/Pagination';
import { QuestionnaireDetailModal } from '../components/QuestionnaireDetailModal';

export const QuestionnairesPage = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const [editingQ, setEditingQ] = useState(null);
  const [editQForm, setEditQForm] = useState({});
  const [deleteConfirmQ, setDeleteConfirmQ] = useState(null);
  const [filters, setFilters] = useState({
    fonction: '', niveau: '', type_questionnaire: '',
    client: '', interlocuteur_client: '', ref_ao: '', tag: '', evaluateur: ''
  });

  useEffect(() => { fetchQuestionnaires(); }, [filters, page]);

  const fetchQuestionnaires = async () => {
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const response = await questionnaireService.getAll(params);
      const data = response.data;
      if (data.items) {
        setQuestionnaires(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      } else {
        setQuestionnaires(data);
        setTotal(data.length);
        setTotalPages(1);
      }
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => { setPage(1); setFilters(newFilters); };

  const handleEditQuestionnaire = (e, q) => {
    e.stopPropagation();
    setEditQForm({ client: q.client || '', ref_ao: q.ref_ao || '', interlocuteur_client: q.interlocuteur_client || '' });
    setEditingQ(q);
  };

  const handleSaveEditQ = async () => {
    try {
      await adminService.updateQuestionnaire(editingQ.id, editQForm);
      toast.success('Questionnaire mis à jour');
      setEditingQ(null);
      fetchQuestionnaires();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleDeleteQuestionnaire = async (id) => {
    try {
      await adminService.deleteQuestionnaire(id);
      toast.success('Questionnaire et tests associés supprimés');
      setDeleteConfirmQ(null);
      fetchQuestionnaires();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleCardClick = async (q) => {
    setSelectedQuestionnaire(q);
    setStatsLoading(true);
    try {
      const response = await questionnaireService.getStats(q.id);
      setStatsData(response.data);
    } catch {
      setStatsData({ total_tests: 0, average_score: 0, candidates: [] });
    } finally {
      setStatsLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedQuestionnaire(null);
    setStatsData(null);
    setSelectedForCompare([]);
    setShowPreview(false);
  };

  return (
    <Layout title="Questionnaires" breadcrumbs={['Accueil', 'Questionnaires']}>
      <div className="mb-6 flex justify-between items-center">
        <Link to="/generer" className="eva-btn-primary flex items-center gap-2" data-testid="generate-new-button">
          <Plus size={18} />
          Nouveau questionnaire
        </Link>
      </div>

      {/* Filters */}
      <div className="eva-card mb-6" data-testid="filters">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={18} className="text-slate-400" />
          <h3 className="font-semibold text-white">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="eva-label">Poste</label>
            <AutocompleteInput value={filters.fonction} onChange={(val) => handleFilterChange({ ...filters, fonction: val })}
              endpoint="/autocomplete/fonctions" className="eva-input" placeholder="Tous" dataTestId="filter-fonction" />
          </div>
          <div>
            <label className="eva-label">Niveau</label>
            <select value={filters.niveau} onChange={(e) => handleFilterChange({ ...filters, niveau: e.target.value })}
              className="eva-input" data-testid="filter-niveau">
              <option value="">Tous</option>
              <option value="Junior">Junior</option>
              <option value="Confirmé">Confirmé</option>
              <option value="Senior">Senior</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Type</label>
            <select value={filters.type_questionnaire} onChange={(e) => handleFilterChange({ ...filters, type_questionnaire: e.target.value })}
              className="eva-input" data-testid="filter-type">
              <option value="">Tous</option>
              <option value="Générique">Générique</option>
              <option value="Appel d'offres">Appel d'offres</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Réf. AO</label>
            <AutocompleteInput value={filters.ref_ao} onChange={(val) => handleFilterChange({ ...filters, ref_ao: val })}
              endpoint="/autocomplete/ref_ao" className="eva-input" placeholder="Tous" dataTestId="filter-ref-ao" />
          </div>
          <div>
            <label className="eva-label">Entreprise</label>
            <AutocompleteInput value={filters.client} onChange={(val) => handleFilterChange({ ...filters, client: val })}
              endpoint="/autocomplete/entreprises" className="eva-input" placeholder="Tous" dataTestId="filter-entreprise" />
          </div>
          <div>
            <label className="eva-label">Opérationnel</label>
            <AutocompleteInput value={filters.interlocuteur_client} onChange={(val) => handleFilterChange({ ...filters, interlocuteur_client: val })}
              endpoint="/autocomplete/interlocuteurs" className="eva-input" placeholder="Tous" dataTestId="filter-interlocuteur" />
          </div>
          <div>
            <label className="eva-label">Tag technique</label>
            <AutocompleteInput value={filters.tag} onChange={(val) => handleFilterChange({ ...filters, tag: val })}
              endpoint="/autocomplete/tags" className="eva-input" placeholder="Tous" dataTestId="filter-tag" />
          </div>
          <div>
            <label className="eva-label">Créé par</label>
            <AutocompleteInput value={filters.evaluateur} onChange={(val) => handleFilterChange({ ...filters, evaluateur: val })}
              endpoint="/autocomplete/evaluateurs" className="eva-input" placeholder="Tous" dataTestId="filter-evaluateur" />
          </div>
        </div>
      </div>

      {/* Questionnaires List */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><p className="text-slate-400">Chargement...</p></div>
      ) : questionnaires.length === 0 ? (
        <div className="text-center py-12" data-testid="empty-state">
          <FileText size={48} className="text-[#E9ECEF] mx-auto mb-4" />
          <p className="text-slate-400 mb-4">Aucun questionnaire trouvé</p>
          <Link to="/generer" className="eva-btn-primary inline-block">Créer votre premier questionnaire</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="questionnaires-list">
          {questionnaires.map((q) => (
            <div key={q.id} className="eva-card cursor-pointer hover:shadow-md transition-shadow relative group"
              onClick={() => handleCardClick(q)} data-testid={`questionnaire-${q.id}`}>
              {isAdmin && (
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button onClick={(e) => handleEditQuestionnaire(e, q)}
                    className="p-1.5 bg-[#0B132B] border border-white/10 rounded-md hover:bg-white/5 transition-colors" data-testid={`edit-q-${q.id}`}>
                    <Pencil size={14} className="text-slate-400" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmQ(q); }}
                    className="p-1.5 bg-[#0B132B] border border-white/10 rounded-md hover:bg-red-50 transition-colors" data-testid={`delete-q-${q.id}`}>
                    <Trash2 size={14} className="text-[#EF4444]" />
                  </button>
                </div>
              )}
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-white text-lg mb-1">{q.fonction}</h3>
                  <p className="text-sm text-slate-400">{q.niveau}{q.ref_ao ? ` • ${q.ref_ao}` : ''} • {q.questions?.length || 0} questions</p>
                </div>
              </div>
              {(q.client || q.ref_ao) && (
                <div className="flex items-center gap-3 text-sm text-slate-300 mb-3">
                  {q.client && <span className="font-medium">{q.client}</span>}
                  {q.client && q.ref_ao && <span className="text-[#E9ECEF]">|</span>}
                  {q.ref_ao && <span className="text-slate-400">AO : {q.ref_ao}</span>}
                </div>
              )}
              {q.tags && q.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {q.tags.slice(0, 3).map((tag, i) => (
                    <span key={`${q.id}-tag-${i}`} className="px-2 py-1 bg-white/5 text-xs text-slate-400 rounded">{tag}</span>
                  ))}
                  {q.tags.length > 3 && <span className="px-2 py-1 text-xs text-slate-400">+{q.tags.length - 3}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && questionnaires.length > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
      )}

      {/* Questionnaire Detail Modal */}
      <QuestionnaireDetailModal
        questionnaire={selectedQuestionnaire}
        statsData={statsData}
        statsLoading={statsLoading}
        showPreview={showPreview}
        setShowPreview={setShowPreview}
        selectedForCompare={selectedForCompare}
        setSelectedForCompare={setSelectedForCompare}
        onClose={closeModal}
        onDelete={(id) => { closeModal(); handleDeleteQuestionnaire(id); }}
        user={currentUser}
      />

      {/* Edit Questionnaire Modal */}
      {editingQ && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-q-modal">
          <div className="bg-[#0B132B] rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Modifier le questionnaire</h3>
              <button onClick={() => setEditingQ(null)} className="p-1 hover:bg-white/5 rounded"><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="eva-label">Client</label>
                <input type="text" value={editQForm.client} onChange={(e) => setEditQForm({ ...editQForm, client: e.target.value })}
                  className="eva-input" data-testid="edit-q-client-input" />
              </div>
              <div>
                <label className="eva-label">Réf. AO</label>
                <input type="text" value={editQForm.ref_ao} onChange={(e) => setEditQForm({ ...editQForm, ref_ao: e.target.value })}
                  className="eva-input" data-testid="edit-q-ref-ao-input" />
              </div>
              <div>
                <label className="eva-label">Opérationnel</label>
                <input type="text" value={editQForm.interlocuteur_client} onChange={(e) => setEditQForm({ ...editQForm, interlocuteur_client: e.target.value })}
                  className="eva-input" data-testid="edit-q-interlocuteur-input" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingQ(null)} className="eva-btn-secondary">Annuler</button>
              <button onClick={handleSaveEditQ} className="eva-btn-primary" data-testid="save-edit-q-button">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmQ && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-q-modal">
          <div className="bg-[#0B132B] rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-slate-400 mb-1">
              Voulez-vous vraiment supprimer le questionnaire <strong>{deleteConfirmQ.fonction}</strong> ?
            </p>
            <p className="text-sm text-[#EF4444] mb-6">
              Tous les tests associés seront également supprimés. Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirmQ(null)} className="eva-btn-secondary">Annuler</button>
              <button onClick={() => handleDeleteQuestionnaire(deleteConfirmQ.id)}
                className="px-4 py-2 bg-[#EF4444] text-white rounded-lg text-sm font-medium hover:bg-[#DC2626] transition-colors" data-testid="confirm-delete-q-button">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
